import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { alertServerError, showNotification } from '../../../../../global/modal/alerts-host/alert-templates';
import { IErrorParsing } from '../../../../../../infrastructure/error-parsing';
import { AudioBridgeProvider, ConferenceModes, ConnectionType, IConnectionDetailsDTO, IConnectionsDAO } from '../../../../../../api/connections.dao';
import { CreateAudioBridgeFormDataVM } from './create-audio-bridge-form-data.viewmodel';
import { AUDIO_BRIDGE_TOAST_MESSAGE } from '../../../../../../constants/audio-bridge-connection.constant';
import { IntlShape } from 'react-intl';
import { IReactIntlProvider } from '../../../../../../infrastructure/react-intl-provider';
import { AudioBridgeProviderLabel } from '../../../../../../infrastructure/utils/common.utils';

class CreateAudioBridgeVM {
    @mobx.observable
    private isFormShown: boolean;
    @mobx.observable
    private isSaving: boolean;
    @mobx.observable
    public createAudioBridgeFormDataVM: CreateAudioBridgeFormDataVM;

    constructor(
        private readonly eventTimeZone: string,
        private readonly startDateTime: Date,
        private readonly endDateTime: Date,
        private readonly audioBridgeName: string,
        public readonly createAudioBridgeConnectionCallback: (connectionDetails?: IConnectionDetailsDTO) => void,
        private event: { eventId: number; accountId: number },
        private readonly isMeetingV2?: boolean,
        private readonly errorParsingService: IErrorParsing = IoC.Container.get(IErrorParsing),
        @IoC.Inject private readonly connectionDAO: IConnectionsDAO = IoC.Container.get(IConnectionsDAO),
        @IoC.Inject private readonly intlProvider: IReactIntlProvider = IoC.Container.get(IReactIntlProvider),
    ) {
        this.isFormShown = false;
        this.isSaving = false;
        this.createAudioBridgeFormDataVM = new CreateAudioBridgeFormDataVM(
            eventTimeZone,
            startDateTime,
            endDateTime,
            audioBridgeName,
        );
    }

    public get intl(): IntlShape { return this.intlProvider.getIntl(); }
    public get isMeetingV2Enabled(): boolean { return this.isMeetingV2; }

    @mobx.action
    public showForm = (): void => {
        this.isFormShown = true;
    };

    @mobx.action
    public hideForm = (): void => {
        this.createAudioBridgeFormDataVM.cleanup();
        this.isFormShown = false;
    };

    public createAudioBridgeHandle = async (showAutoRegAndAutoSyncToggles: boolean): Promise<void> => {
        let connectionDetails: IConnectionDetailsDTO;
        if (!this.createAudioBridgeFormDataVM.checkForm() || this.isSaving) {
            return;
        }

        mobx.runInAction(() => {
            this.isSaving = true;
        });
        showNotification(AUDIO_BRIDGE_TOAST_MESSAGE);

        try {
            const audioBridge = this.createAudioBridgeFormDataVM.getAPIDTO();
            const response = await this.connectionDAO.scheduleNewAudioBridge({ ...audioBridge, ...this.event });
            connectionDetails = {
                ...response,
                type: ConnectionType.AudioBridge,
                audioBridgeID: response.audioBridgeID,
                conferenceId: response.conferenceID,
                operatorPin: response.operatorPin,
                audioProviderId: response.audioProviderId,
                audioBridgeName: response.name,
                autoSyncEnabled: this.isMeetingV2Enabled && showAutoRegAndAutoSyncToggles ? this.createAudioBridgeFormDataVM.getAutoSyncEnabled() : false,
                automaticallyRecordEnabled: this.createAudioBridgeFormDataVM.getAutomaticallyRecordEnabled()
            };
        } catch (error) {
            const errorMessage = this.errorParsingService.extractMessage(error);
            alertServerError('Error', errorMessage);
            return;
        } finally {
            mobx.runInAction(() => {
                this.isSaving = false;
            });
        }

        this.createAudioBridgeConnectionCallback(connectionDetails);
        this.hideForm();
    };

    public openAudioBridgeConnectionForm(connectionDetails: IConnectionDetailsDTO): void {
        connectionDetails.audioBridgeName = this.audioBridgeName;
        this.createAudioBridgeConnectionCallback(connectionDetails);
        this.hideForm();
    };

    public getIsFormShown = (): boolean => {
        return this.isFormShown;
    };

    public getIsFormSaving = (): boolean => {
        return this.isSaving;
    };

    public getProviderOptions(): { value: AudioBridgeProvider; label: AudioBridgeProvider }[] {
        return [AudioBridgeProvider.ZOOM, AudioBridgeProvider.TURBOBRIDGE, AudioBridgeProvider.OTHER]
            .map(val => ({ value: val, label: AudioBridgeProviderLabel[val] as AudioBridgeProvider }));
    }

    public getConferenceModeOptions(): { value: ConferenceModes; label: ConferenceModes }[] {
        return [ConferenceModes.Hosts, ConferenceModes.Presentation, ConferenceModes.QA, ConferenceModes.Conversation]
            .map(val => ({ value: val, label: val }));
    }
}

export { CreateAudioBridgeVM };